import React, { Component, ReactNode, ErrorInfo } from 'react';
import { Box, Typography, Button, Alert, AlertTitle, Paper, Stack, Divider } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import LoginIcon from '@mui/icons-material/Login';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

export interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onRetry?: () => void;
  onLoginRedirect?: () => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  retryCount: number;
}

interface AuthenticationError extends Error {
  statusCode?: number;
  isAuthError?: boolean;
  isNetworkError?: boolean;
}

/**
 * Error boundary specifically designed for authentication-related errors.
 * Provides fallback UI and recovery options for session/auth failures.
 */
export class AuthenticationErrorBoundary extends Component<Props, State> {
  private readonly maxRetries = 3;

  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log authentication errors for monitoring
    console.error('🔒 Authentication Error Boundary caught an error:', error, errorInfo);

    this.setState({
      error,
      errorInfo,
    });

    // Report to error monitoring service if available
    if (this.isAuthenticationError(error)) {
      this.reportAuthError(error, errorInfo);
    }
  }

  private isAuthenticationError = (error: Error): boolean => {
    const authError = error as AuthenticationError;

    // Check for authentication-specific errors
    if (authError.isAuthError) return true;
    if (authError.statusCode === 401 || authError.statusCode === 403) return true;

    // Check error messages for auth-related keywords
    const authKeywords = [
      'authentication',
      'token',
      'session',
      'unauthorized',
      'forbidden',
      'login',
      'refresh',
      'cognito',
    ];

    const errorMessage = error.message.toLowerCase();
    return authKeywords.some(keyword => errorMessage.includes(keyword));
  };

  private isNetworkError = (error: Error): boolean => {
    const authError = error as AuthenticationError;
    return (
      (authError.isNetworkError ?? false) ||
      error.message.includes('network') ||
      error.message.includes('fetch') ||
      error.message.includes('offline')
    );
  };

  private reportAuthError = (error: Error, errorInfo: ErrorInfo) => {
    // In a real application, send to monitoring service like Sentry, CloudWatch, etc.
    const errorReport = {
      timestamp: new Date().toISOString(),
      error: {
        message: error.message,
        stack: error.stack,
        name: error.name,
      },
      errorInfo: {
        componentStack: errorInfo.componentStack,
      },
      userAgent: navigator.userAgent,
      url: window.location.href,
      retryCount: this.state.retryCount,
    };

    console.warn('📊 Auth error reported:', errorReport);

    // TODO: Integrate with actual error monitoring service
    // errorMonitoring.captureException(error, errorReport);
  };

  private handleRetry = () => {
    const newRetryCount = this.state.retryCount + 1;

    if (newRetryCount <= this.maxRetries) {
      this.setState({
        hasError: false,
        error: null,
        errorInfo: null,
        retryCount: newRetryCount,
      });

      // Call custom retry handler if provided
      if (this.props.onRetry) {
        this.props.onRetry();
      }
    }
  };

  private handleLoginRedirect = () => {
    if (this.props.onLoginRedirect) {
      this.props.onLoginRedirect();
    } else {
      // Default behavior: redirect to login
      window.location.href = '/login?reason=error_boundary';
    }
  };

  private renderErrorDetails = () => {
    if (!this.state.error) return null;

    const isAuthError = this.isAuthenticationError(this.state.error);
    const isNetworkError = this.isNetworkError(this.state.error);

    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Error Details:
        </Typography>
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
          {this.state.error.message}
        </Typography>

        {(isAuthError || isNetworkError) && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Type: {isAuthError ? 'Authentication Error' : 'Network Error'}
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  private renderRetrySection = () => {
    const canRetry = this.state.retryCount < this.maxRetries;
    const isNetworkError = this.state.error && this.isNetworkError(this.state.error);

    return (
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 3 }}>
        {canRetry && (
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={this.handleRetry}
            disabled={!canRetry}
          >
            Retry ({this.maxRetries - this.state.retryCount} remaining)
          </Button>
        )}

        {(this.state.retryCount >= this.maxRetries || !isNetworkError) && (
          <Button
            variant="contained"
            startIcon={<LoginIcon />}
            onClick={this.handleLoginRedirect}
            color="primary"
          >
            Go to Login
          </Button>
        )}
      </Stack>
    );
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default authentication error fallback UI
      const isAuthError = this.state.error && this.isAuthenticationError(this.state.error);
      const isNetworkError = this.state.error && this.isNetworkError(this.state.error);

      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '50vh',
            p: 3,
          }}
        >
          <Paper
            elevation={3}
            sx={{
              p: 4,
              maxWidth: 600,
              width: '100%',
              textAlign: 'center',
            }}
          >
            <ErrorOutlineIcon
              sx={{
                fontSize: 64,
                color: 'error.main',
                mb: 2,
              }}
            />

            <Typography variant="h4" gutterBottom color="error">
              {isAuthError
                ? 'Authentication Error'
                : isNetworkError
                  ? 'Connection Error'
                  : 'Something went wrong'}
            </Typography>

            <Alert severity="error" sx={{ mt: 2, textAlign: 'left' }}>
              <AlertTitle>
                {isAuthError
                  ? 'Session Problem'
                  : isNetworkError
                    ? 'Network Issue'
                    : 'Application Error'}
              </AlertTitle>
              {isAuthError
                ? 'There was a problem with your authentication session. Please try logging in again.'
                : isNetworkError
                  ? 'Unable to connect to the server. Please check your internet connection and try again.'
                  : 'An unexpected error occurred. Please refresh the page or try logging in again.'}
            </Alert>

            {process.env.NODE_ENV === 'development' && this.renderErrorDetails()}

            <Divider sx={{ my: 3 }} />

            {this.renderRetrySection()}

            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              If this problem persists, please contact support.
            </Typography>
          </Paper>
        </Box>
      );
    }

    return this.props.children;
  }
}
export default AuthenticationErrorBoundary;
